<template>
  <div class="error-page-wrapper">
    <HeaderComponent
      v-bind="headerProps"
    />

    <T3BackendLayout
      v-if="hasFallbackContent"
      :name="pageData!.appearance.backendLayout"
      :content="pageData!.content"
    />

    <ErrorPage
      v-else
      :error="props.error"
      :translation="translation[currentLocale as keyof typeof translation]"
      :is-debug="Boolean(debug)"
      :is-main-page="isMainPage"
    />

    <FooterComponent
      v-bind="footerProps"
    />
  </div>
</template>

<script setup lang="ts">
import type { NuxtError } from '#app'
import { computed, reactive, useCurrentLocale, useHead, useRoute, useT3Api, useT3Options, useT3Page, watch } from '#imports'

import { usePageDataFooter } from './composables/usePageDataFooter'
import { usePageDataHeader } from './composables/usePageDataHeader'

const props = defineProps<{ error: NuxtError }>()

const { options } = useT3Options()
const { initialData } = useT3Api()
const { query } = useRoute()
const currentLocale = useCurrentLocale()

const { pageData } = await useT3Page()

const hasFallbackContent = computed(() => {
  const content = pageData.value?.content
  if (Array.isArray(content)) {
    return Boolean(content.length)
  }

  return Boolean(content?.colPos0?.length)
})

const headerProps = reactive(usePageDataHeader(pageData, initialData))
const footerProps = reactive(usePageDataFooter(initialData))

const { debug } = query
const isMainPage = computed(() => !options.api.baseUrl?.includes('t3headless'))

watch(pageData, (pageData) => {
  const pageDataSeo = pageData?.seo
  if (!pageDataSeo) {
    return
  }

  useHead(pageDataSeo || {})
}, { immediate: true, deep: true })

const translation = {
  en: {
    error: 'Error',
    header: 'Unfortunately, the page you\'re looking for could not be found.',
    text: 'Check out our {{link}}.',
    linkArray: [
      {
        label: 'home page',
        href: '/'
      }
    ]
  },
  pl: {
    error: 'Błąd',
    header: 'Przepraszamy, nie udało nam się znaleźć szukanej strony.',
    text: 'Zapraszamy do odwiedzenia naszej {{link}}.',
    linkArray: [
      {
        label: 'strony głównej',
        href: '/pl'
      }
    ]
  },
  de: {
    error: 'Error',
    header: 'Die von Ihnen gesuchte Seite konnte leider nicht gefunden werden.',
    text: 'Besuchen Sie unsere {{link}}.',
    linkArray: [
      {
        label: 'Homepage',
        href: '/de'
      }
    ]
  }
}
</script>

<style lang="scss" scoped>
.error-page-wrapper {
  display: grid;
  grid-template-rows: auto 1fr auto;
  min-height: 100vh;
  min-height: 100dvh;
}
</style>
